import { Link } from "react-router-dom";
import type { ReactNode } from "react";

interface PageHeroProps {
  eyebrow: string;
  title: ReactNode;
  intro: string;
  image: string;
  imageAlt: string;
  primaryLabel?: string;
  primaryTo?: string;
  secondaryLabel?: string;
  secondaryTo?: string;
}

export default function PageHero({
  eyebrow,
  title,
  intro,
  image,
  imageAlt,
  primaryLabel = "Book Appointment",
  primaryTo = "/book",
  secondaryLabel,
  secondaryTo,
}: PageHeroProps) {
  return (
    <section className="px-3 md:px-4 pt-3 md:pt-4">
      <div className="relative w-full min-h-[380px] sm:min-h-[420px] md:min-h-[520px] rounded-2xl md:rounded-3xl overflow-hidden">
        <img
          src={image}
          alt={imageAlt}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-950/45 to-primary-950/80"></div>

        <div className="relative z-10 flex flex-col justify-end h-full min-h-[380px] sm:min-h-[420px] md:min-h-[520px] px-4 sm:px-6 md:px-12 py-8 sm:py-10 md:py-14">
          <div className="inline-flex self-start items-center gap-2 mb-4 sm:mb-5 px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/25 text-background-50 text-[10px] sm:text-xs uppercase tracking-[0.16em]">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
            {eyebrow}
          </div>
          <h1 className="font-heading font-semibold text-background-50 text-[32px] sm:text-[40px] md:text-[56px] lg:text-[72px] leading-[1.02] tracking-tight max-w-4xl">
            {title}
          </h1>
          <p className="mt-3 sm:mt-4 md:mt-6 max-w-xl text-sm sm:text-base md:text-lg text-background-100 leading-relaxed">
            {intro}
          </p>
          <div className="mt-5 sm:mt-6 md:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3">
            <Link
              to={primaryTo}
              className="group inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3.5 sm:py-4 rounded-full bg-accent-500 text-primary-950 text-sm md:text-base font-semibold hover:bg-accent-600 transition-all hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
            >
              {primaryLabel}
              <i className="ri-arrow-right-up-line transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
            </Link>
            {secondaryLabel && secondaryTo && (
              <Link
                to={secondaryTo}
                className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3.5 sm:py-4 rounded-full bg-background-50/10 border border-background-50/25 text-background-50 text-sm md:text-base font-semibold hover:bg-background-50/20 transition-all cursor-pointer whitespace-nowrap"
              >
                {secondaryLabel}
                <i className="ri-arrow-right-line"></i>
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}